import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { LocaleSwitcher } from '@/components/layout/locale-switcher';
import { ModeSwitcher } from '@/components/theme/mode-switcher';
import { Button } from '@/components/ui/button';
import { CreditBadge } from '@/components/shared/credit-badge';
import { websiteConfig } from '@/config/website';
import { getLocale, localizeHref } from '@/lib/locale';
import { Routes } from '@/lib/routes';
import { navigateWithSafeBack } from '@/lib/safe-back';
import { m } from '@/locale/paraglide/messages';
import { IconArrowLeft } from '@tabler/icons-react';
import { Link, useRouter } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import React, { useCallback, useEffect } from 'react';

export interface DashboardBreadcrumbItem {
  label: string;
  href?: string;
  isCurrentPage?: boolean;
}

interface DashboardHeaderProps {
  breadcrumbs?: DashboardBreadcrumbItem[];
  center?: ReactNode;
  actions?: ReactNode;
  showBack?: boolean;
  showCredits?: boolean;
  showLocale?: boolean;
  showMode?: boolean;
  showSidebarTrigger?: boolean;
}

function isEditableTarget(target: EventTarget | null) {
  const element = target as HTMLElement | null;
  const tag = element?.tagName;

  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    element?.isContentEditable === true
  );
}

function hasBlockingOverlay() {
  if (typeof document === 'undefined') return false;

  return Boolean(
    document.querySelector(
      [
        '[role="dialog"]',
        '[data-slot="dialog-content"]',
        '[data-slot="drawer-content"]',
        '[data-slot="sheet-content"]',
        '[data-slot="popover-content"]',
      ].join(', ')
    )
  );
}

export function DashboardHeader({
  breadcrumbs,
  center,
  actions,
  showBack = false,
  showCredits = true,
  showLocale = true,
  showMode = true,
  showSidebarTrigger = true,
}: DashboardHeaderProps) {
  const router = useRouter();
  const showModeSwitch = websiteConfig.ui?.mode?.enableSwitch ?? false;
  const onBack = useCallback(() => {
    navigateWithSafeBack({
      back: () => router.history.back(),
      fallback: localizeHref(Routes.Root, { locale: getLocale() }),
      navigate: (to) => router.navigate({ to, search: {} }),
    });
  }, [router]);

  useEffect(() => {
    if (!showBack) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || isEditableTarget(event.target)) return;
      if (hasBlockingOverlay()) return;

      event.preventDefault();
      onBack();
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onBack, showBack]);

  return (
    <header className="sticky top-0 z-50 bg-background md:rounded-t-xl flex h-(--header-height) shrink-0 items-center border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
      <div className="flex w-full min-w-0 items-center gap-2 px-4 lg:px-6">
        {showSidebarTrigger ? (
          <SidebarTrigger className="-ml-1 shrink-0" />
        ) : null}

        {showBack && (
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            onClick={onBack}
            aria-label={m.common_back()}
            className="shrink-0"
          >
            <IconArrowLeft className="size-4 rtl:rotate-180" />
          </Button>
        )}

        <div className="flex min-w-0 flex-1 items-center">
          {center ??
            (breadcrumbs && breadcrumbs.length > 0 ? (
              <Breadcrumb className="min-w-0 flex-1">
                <BreadcrumbList className="text-base font-medium">
                  {breadcrumbs.map((item, index) => (
                    <React.Fragment key={`breadcrumb-${index}`}>
                      {index > 0 && (
                        <BreadcrumbSeparator
                          key={`sep-${index}`}
                          className="hidden md:block"
                        />
                      )}
                      <BreadcrumbItem
                        key={`item-${index}`}
                        className={
                          index < breadcrumbs.length - 1
                            ? 'hidden md:block'
                            : ''
                        }
                      >
                        {item.isCurrentPage ? (
                          <BreadcrumbPage className="font-semibold">
                            {item.label}
                          </BreadcrumbPage>
                        ) : item.href ? (
                          <BreadcrumbLink render={<Link to={item.href} />}>
                            {item.label}
                          </BreadcrumbLink>
                        ) : (
                          item.label
                        )}
                      </BreadcrumbItem>
                    </React.Fragment>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>
            ) : null)}
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-3 pl-4">
          {actions}
          {showCredits ? <CreditBadge /> : null}
          {showLocale ? <LocaleSwitcher /> : null}
          {showMode && showModeSwitch ? <ModeSwitcher /> : null}
        </div>
      </div>
    </header>
  );
}
